import React, { useState, useEffect, useCallback } from 'react';
import { UserPlus, Send, Clock, Check, X, RefreshCw, Mail } from 'lucide-react';
import { toast } from '../ui/simple-toaster';
import { api } from '../../utils/api';

const STATUS_STYLES = {
  PENDING: 'bg-amber-500/20 text-amber-400',
  ACCEPTED: 'bg-emerald-500/20 text-emerald-400',
  DECLINED: 'bg-red-500/20 text-red-400',
  CANCELLED: 'bg-slate-500/20 text-slate-400',
};

const FleetInvitesPanel = () => {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const loadInvites = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api('/group-view/fleet-invites/outgoing');
      setInvites(data.invites || []);
    } catch {
      // Table may not exist
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadInvites(); }, [loadInvites]);

  const sendInvite = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter the driver\'s email');
      return;
    }
    setSending(true);
    try {
      await api('/group-view/fleet-invites', {
        method: 'POST',
        body: JSON.stringify({
          target_email: email.trim(),
          message: message || undefined,
        }),
      });
      toast.success('Invite sent successfully');
      setEmail('');
      setMessage('');
      setShowForm(false);
      loadInvites();
    } catch (err) {
      toast.error(err.message || 'Failed to send invite');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div data-testid="fleet-invites-panel">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-lg font-semibold text-white">Fleet Invites</h2>
          <p className="text-sm text-slate-400">Invite Owner/Operator drivers to join your fleet</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 rounded-lg text-sm font-medium transition"
            data-testid="new-invite-btn"
          >
            <UserPlus className="w-4 h-4" />
            New Invite
          </button>
          <button onClick={loadInvites} className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700 transition" data-testid="invites-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* New Invite Form */}
      {showForm && (
        <form onSubmit={sendInvite} className="bg-slate-800/70 rounded-2xl border border-slate-700 p-4 mb-6 space-y-3" data-testid="invite-form">
          <h3 className="text-white text-sm font-medium">Invite Driver to Fleet</h3>
          <p className="text-xs text-slate-400">Enter the email address of the Owner/Operator driver you want to invite.</p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Driver Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="driver@example.com"
                  className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                  data-testid="invite-email-input"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Message (optional)</label>
              <input
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="We'd like you to join our fleet..."
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                data-testid="invite-message-input"
              />
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-4 py-2 text-slate-400 text-sm rounded-lg hover:bg-slate-700 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={sending}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium rounded-lg transition disabled:opacity-50"
              data-testid="send-invite-btn"
            >
              <Send className="w-4 h-4" />
              {sending ? 'Sending...' : 'Send Invite'}
            </button>
          </div>
        </form>
      )}

      {/* Invites List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full" />
        </div>
      ) : invites.length === 0 ? (
        <div className="bg-slate-800/50 rounded-2xl border border-slate-700 p-8 text-center">
          <UserPlus className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">No fleet invites sent yet</p>
          <p className="text-xs text-slate-500 mt-1">Click "New Invite" to invite an Owner/Operator driver to join your fleet</p>
        </div>
      ) : (
        <div className="bg-slate-800/50 rounded-2xl border border-slate-700 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left p-4 text-slate-400 font-medium">Driver Email</th>
                <th className="text-left p-4 text-slate-400 font-medium hidden sm:table-cell">Message</th>
                <th className="text-center p-4 text-slate-400 font-medium">Status</th>
                <th className="text-left p-4 text-slate-400 font-medium hidden md:table-cell">Sent</th>
                <th className="text-left p-4 text-slate-400 font-medium hidden md:table-cell">Responded</th>
              </tr>
            </thead>
            <tbody>
              {invites.map(inv => (
                <tr key={inv.invite_id} className="border-b border-slate-700/50 hover:bg-slate-700/20 transition">
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <span className="text-white">{inv.target_email}</span>
                    </div>
                  </td>
                  <td className="p-4 hidden sm:table-cell text-slate-400 text-xs max-w-[200px] truncate">
                    {inv.message || '-'}
                  </td>
                  <td className="p-4 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[inv.status] || STATUS_STYLES.PENDING}`}>
                      {inv.status === 'PENDING' && <Clock className="w-3 h-3 inline mr-1" />}
                      {inv.status === 'ACCEPTED' && <Check className="w-3 h-3 inline mr-1" />}
                      {inv.status === 'DECLINED' && <X className="w-3 h-3 inline mr-1" />}
                      {inv.status}
                    </span>
                  </td>
                  <td className="p-4 hidden md:table-cell text-slate-400 text-xs">{formatDate(inv.created_at)}</td>
                  <td className="p-4 hidden md:table-cell text-slate-400 text-xs">{formatDate(inv.responded_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FleetInvitesPanel;
